import { useEffect, Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAuthStore } from '@stores/authStore'
import { usePreventNumberInputWheel } from '@hooks/usePreventNumberInputWheel'
import { settingsApi } from '@services/api'
import { applyTheme } from '@/config/themePresets'
import { KYC_STUB_ROUTE_ENABLED } from '@/constants/kyc'
// import { isForceV1 } from '@/config/designV2'  // deactivated with v2 guard

// Layouts (always loaded — used by all routes)
import AuthLayout from '@components/layout/AuthLayout'
import DashboardLayout from '@components/layout/DashboardLayout'
import PropertyRedirect from '@components/layout/PropertyRedirect'
import ErrorBoundary from '@components/ui/ErrorBoundary'

// V2 Layout — archived (v1 layout used for all users), file kept under _archive for future reference
// const DashboardLayoutV2 = lazy(() => import('@components/layout/_archive/DashboardLayoutV2'))

// Auth Pages (always loaded — entry point for all users)
import LoginPage from '@pages/auth/LoginPage'
import ForgotPasswordPage from '@pages/auth/ForgotPasswordPage'
import PasswordSetupPage from '@pages/auth/PasswordSetupPage'
import MagicLinkPage from '@pages/auth/MagicLinkPage'

// Lazy-loaded Pages (code-split by feature area)
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage'))
const TasksPage = lazy(() => import('@pages/tasks/TasksPage'))
const CEOAgentComposerPage = lazy(() => import('@pages/tasks/CEOAgentComposerPage'))
const KnowledgeBoardPage = lazy(() => import('@pages/tasks/KnowledgeBoardPage'))
const EmailsPage = lazy(() => import('@pages/emails/EmailsPage'))

// Admin
const SchemaPage = lazy(() => import('@pages/admin/SchemaPage'))
const ZefixSyncPage = lazy(() => import('@pages/admin/ZefixSyncPage'))
const RegulatedWhitelistPage = lazy(() => import('@pages/admin/RegulatedWhitelistPage'))
const KycReviewPage = lazy(() => import('@pages/admin/KycReviewPage'))
const MediaAssetsPage = lazy(() => import('@pages/admin/MediaAssetsPage'))
const AITestPage = lazy(() => import('@pages/admin/AITestPage'))
const EmailTemplatesPage = lazy(() => import('@pages/admin/EmailTemplatesPage'))
const MandantenPage = lazy(() => import('@pages/admin/MandantenPage'))
const SystemSettingsPage = lazy(() => import('@pages/admin/settings/SystemSettingsPage'))
const InvestorGroupsPage = lazy(() => import('@pages/admin/settings/InvestorGroupsPage'))
const CommissionSettingsPage = lazy(() => import('@pages/admin/settings/CommissionSettingsPage'))
const ProcessVisualizationPage = lazy(() => import('@pages/admin/settings/ProcessVisualizationPage'))
const E2EReportsPage = lazy(() => import('@pages/admin/settings/E2EReportsPage'))
const AnalysesAdminPage = lazy(() => import('@pages/admin/settings/AnalysesAdminPage'))
const VouchersPage = lazy(() => import('@pages/admin/settings/VouchersPage'))
const CEOAgentPage = lazy(() => import('@pages/admin/CEOAgentPage'))

// Sales
const PropertiesPage = lazy(() => import('@pages/sales/PropertiesPage'))
const UnifiedPropertyProcess = lazy(() => import('@pages/sales/UnifiedPropertyProcess'))
const AIJobsPage = lazy(() => import('@pages/sales/AIJobsPage'))
const AILearningPage = lazy(() => import('@pages/sales/AILearningPage'))
const ListingsPage = lazy(() => import('@pages/sales/ListingsPage'))
const ListingDetailPage = lazy(() => import('@pages/sales/ListingDetailPage'))
const ContractsListPage = lazy(() => import('@pages/sales/ContractsListPage'))
const ContractWizardV3Unified = lazy(() => import('@pages/sales/ContractWizardV3Unified'))
const SearchMandateWizard = lazy(() => import('@pages/sales/SearchMandateWizard'))
const ContractDetailPage = lazy(() => import('@pages/sales/ContractDetailPage'))
const CountersignPage = lazy(() => import('@pages/sales/CountersignPage'))
const ContactsPage = lazy(() => import('@pages/sales/ContactsPage'))
// Google/Microsoft import pages are now embedded in the unified ContactImportPage
// Old routes redirect to /crm/contacts/import
const CampaignsListPage = lazy(() => import('@pages/sales/CampaignsListPage'))
const CampaignCreatePage = lazy(() => import('@pages/sales/CampaignCreatePage'))
const CampaignDetailPage = lazy(() => import('@pages/sales/CampaignDetailPage'))
const QualityGatePage = lazy(() => import('@pages/sales/QualityGatePage'))

// Investor
const SearchPage = lazy(() => import('@pages/investor/SearchPage'))
const InvestorPropertyDetailPage = lazy(() => import('@pages/investor/PropertyDetailPage'))
const ListingTeaserPage = lazy(() => import('@pages/investor/ListingTeaserPage'))
const ListingDossierPage = lazy(() => import('@pages/investor/ListingDossierPage'))
const FavoritesPage = lazy(() => import('@pages/investor/FavoritesPage'))
const MyOffersPage = lazy(() => import('@pages/investor/MyOffersPage'))
const SearchProfilePage = lazy(() => import('@pages/investor/SearchProfilePage'))
const AnalysesPage = lazy(() => import('@pages/investor/AnalysesPage'))
const AnalysisNewPage = lazy(() => import('@pages/investor/AnalysisNewPage'))
const InvestorContractsPage = lazy(() => import('@pages/investor/ContractsPage'))
const InvestorContractDetailPage = lazy(() => import('@pages/investor/ContractDetailPage'))
// CRM
const CRMOrganizationsPage = lazy(() => import('@pages/crm/CRMOrganizationsPage'))
const FundRadarPage = lazy(() => import('@pages/crm/FundRadarPage'))
const OrganizationDetailPage = lazy(() => import('@pages/crm/OrganizationDetailPage'))
const CRMContactsPage = lazy(() => import('@pages/crm/ContactsPage'))
const ContactDetailPage = lazy(() => import('@pages/crm/ContactDetailPage'))
const CRMActivitiesPage = lazy(() => import('@pages/crm/ActivitiesPage'))
const ContactImportPage = lazy(() => import('@pages/crm/ContactImportPage'))
const ContactDedupPage = lazy(() => import('@pages/crm/ContactDedupPage'))

// Seller
const SellerPropertiesPage = lazy(() => import('@pages/seller/SellerPropertiesPage'))

// Developer (Kunden-IT: eigene API-Keys + Anleitung, #1239)
const DeveloperPage = lazy(() => import('@pages/developer/DeveloperPage'))

// Settings (unified — all roles, with role-based tabs)
const SettingsPage = lazy(() => import('@pages/settings/ProfilePage'))
const EmailOAuthCallbackPage = lazy(() => import('@pages/settings/EmailOAuthCallbackPage'))

// Notifications (all authenticated users)
const NotificationsPage = lazy(() => import('@pages/notifications/NotificationsPage'))

// CRM Campaigns (v2 only — no CRM-prefix variant exists)
const CrmCampaignsPage = lazy(() => import('@pages/crm/CampaignsPage'))

// Dev Command & Control (admin-only)
const DevLayout = lazy(() => import('@pages/dev/DevLayout'))
const DevCommandCenter = lazy(() => import('@pages/dev/DevCommandCenter'))

// Public
const PublicSigningPage = lazy(() => import('@pages/public/PublicSigningPage'))
const PublicSigningDocumentPage = lazy(() => import('@pages/public/PublicSigningDocumentPage'))
const OnboardingPage = lazy(() => import('@pages/public/OnboardingPage'))
const PublicTeaserPage = lazy(() => import('@pages/public/PublicTeaserPage'))
const AgbPage = lazy(() => import('@pages/public/AgbPage'))
const DeveloperSignupPage = lazy(() => import('@pages/auth/DeveloperSignupPage'))
const KycStubPage = lazy(() => import('@pages/public/KycStubPage'))
const KycMobilePage = lazy(() => import('@pages/public/KycMobilePage'))
const SignMobilePage = lazy(() => import('@pages/public/SignMobilePage'))
const HelpPage = lazy(() => import('@pages/public/HelpPage'))

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64 animate-fade-in">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--v2-border)] border-t-[var(--v2-brand)]" />
      </div>
    </div>
  )
}

/**
 * Alte Seite «Adressen» (#1226) — aufgelöst. Die vier Reiter sind jetzt eigene
 * CRM-Seiten; alte Lesezeichen und Mail-Links landen auf dem passenden Ziel.
 * Recherche-Leads sind mit #1225 unqualifizierte Organisationen geworden.
 */
const ADDRESSES_TAB_TARGETS: Record<string, string> = {
  companies: '/crm/organizations',
  contacts: '/crm/contacts',
  'contact-import': '/crm/contacts/import',
  'linkedin-import': '/crm/contacts/import',
  'contact-dedup': '/crm/contacts/dedup',
  'research-leads': '/crm/organizations?qualification=unqualified',
}

function AddressesRedirect() {
  const [searchParams] = useSearchParams()
  const tab = searchParams.get('tab') ?? ''
  return <Navigate to={ADDRESSES_TAB_TARGETS[tab] ?? '/crm/organizations'} replace />
}

/**
 * Rolle «developer» (#1239): Kunden-IT verwaltet nur eigene API-Keys und das
 * Profil — CRM, Verkauf und Dashboard bleiben zu (sonst 403-Toasts).
 * Der Rollen-Union im Auth-Store kennt `developer` bewusst nicht
 * («nicht intern»), darum der Vergleich über `string`.
 */
const DEVELOPER_PATHS = ['/developer', '/settings']
const isDeveloperRole = (role?: string): boolean => role === 'developer'

// Protected Route Component
function ProtectedRoute({
  children,
  allowedRoles
}: {
  children: React.ReactNode
  allowedRoles?: string[]
}) {
  const { isAuthenticated, user } = useAuthStore()
  const location = useLocation()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (
    isDeveloperRole(user?.role) &&
    !DEVELOPER_PATHS.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/developer" replace />
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Public Route (redirect to dashboard if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()

  if (isAuthenticated) {
    return <Navigate to={isDeveloperRole(user?.role) ? '/developer' : '/dashboard'} replace />
  }

  return <>{children}</>
}

// V2-Only Route Guard — deactivated (CRM routes accessible for all users), kept for future reactivation
// function V2OnlyRoute({ children }: { children: React.ReactNode }) {
//   const { user } = useAuthStore()
//   const isV2 = user?.design_version === 'v2' && !isForceV1()
//   if (!isV2) { return <Navigate to="/dashboard" replace /> }
//   return <>{children}</>
// }

// Layout selector: always v1 layout (v2 layout deactivated — CRM routes use v1 shell)
function LayoutSelector() {
  return <DashboardLayout />
}

function App() {
  // STORY-917: Mausrad darf fokussierte Zahlen-Inputs app-weit nicht verändern
  usePreventNumberInputWheel()

  useEffect(() => {
    // Force v2 CSS variables to light mode so CRM pages render correctly on v1's white background
    document.documentElement.dataset.v2Mode = 'light'

    settingsApi.getTheme()
      .then((res) => applyTheme(res.data.preset))
      .catch(() => {})  // CSS defaults already set in :root
  }, [])

  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          {/* Selbstregistrierung Entwickler-Zugang (#1243) — öffentlich */}
          <Route
            path="/register/developer"
            element={
              <PublicRoute>
                <DeveloperSignupPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password/:token"
            element={<PasswordSetupPage />}
          />
          <Route
            path="/password-setup/:token"
            element={<PasswordSetupPage />}
          />
          <Route
            path="/auth/set-password/:token"
            element={<PasswordSetupPage />}
          />
          <Route
            path="/auth/magic-link/:token"
            element={<MagicLinkPage />}
          />
        </Route>

        {/* Protected Routes — layout auto-selected (v1 or v2) */}
        <Route
          element={
            <ProtectedRoute>
              <LayoutSelector />
            </ProtectedRoute>
          }
        >
          {/* Dashboard - All authenticated users */}
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/:taskId/agent" element={<CEOAgentComposerPage />} />
          <Route path="/tasks/knowledge-board" element={<KnowledgeBoardPage />} />
          <Route path="/emails" element={<EmailsPage />} />

          {/* Settings - All authenticated users (role-based tabs) */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/email/callback/:provider" element={<EmailOAuthCallbackPage />} />
          <Route path="/profile" element={<Navigate to="/settings" replace />} />

          {/* Entwickler-Zugang — Kunden-IT (#1239), Admins finden die Seite über die Verwaltung */}
          <Route
            path="/developer"
            element={
              <ProtectedRoute allowedRoles={['developer', 'admin', 'tenant_admin']}>
                <DeveloperPage />
              </ProtectedRoute>
            }
          />

          {/* Notifications - All authenticated users */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* Shared property links (notifications, tasks) — role-adaptive redirect */}
          <Route path="/properties/:id" element={<PropertyRedirect />} />

          {/* Legacy redirects — alte Adressen-Seite (#1226) */}
          <Route path="/addresses" element={<AddressesRedirect />} />
          <Route path="/admin/companies" element={<Navigate to="/crm/organizations" replace />} />
          <Route path="/admin/contacts" element={<Navigate to="/crm/contacts" replace />} />

          {/* Settings Sub-Routes */}
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
                <Navigate to="/admin/settings/mandanten" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/users"
            element={<Navigate to="/admin/settings/mandanten" replace />}
          />
          <Route
            path="/admin/settings/users/:userId"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/signatories"
            element={<Navigate to="/admin/settings/mandanten" replace />}
          />
          <Route
            path="/admin/settings/schema"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SchemaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/zefix-sync"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ZefixSyncPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/regulated-whitelist"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <RegulatedWhitelistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/kyc-review"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <KycReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/media"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <MediaAssetsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/ai"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AITestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/investor-groups"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <InvestorGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/system"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <SystemSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/commissions"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CommissionSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/processes"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ProcessVisualizationPage />
              </ProtectedRoute>
            }
          />
          {/* Objektanalysen aller Firmen mit Rückbuchung (#1247) */}
          <Route
            path="/admin/settings/analyses"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AnalysesAdminPage />
              </ProtectedRoute>
            }
          />
          {/* Gutscheincodes anlegen und verwalten (#1259) */}
          <Route
            path="/admin/settings/vouchers"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <VouchersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/e2e-reports"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <E2EReportsPage />
              </ProtectedRoute>
            }
          />

          {/* Sales Routes */}
          <Route
            path="/sales/properties"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <PropertiesPage />
              </ProtectedRoute>
            }
          />
          {/* Unified Property Process — single page for create + edit (Issue #476) */}
          <Route
            path="/sales/properties/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <UnifiedPropertyProcess />
              </ProtectedRoute>
            }
          />
          {/* Legacy redirects (kept for bookmarks/links) */}
          <Route
            path="/sales/properties/wizard"
            element={<Navigate to="/sales/properties/new" replace />}
          />
          <Route
            path="/sales/properties/ai-upload"
            element={<Navigate to="/sales/properties/new" replace />}
          />
          <Route
            path="/sales/properties/unified/new"
            element={<Navigate to="/sales/properties/new" replace />}
          />
          <Route
            path="/sales/ai-jobs"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <AIJobsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/ai-learning"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <AILearningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/properties/:id"
            element={
              <ProtectedRoute
                allowedRoles={['admin', 'sales', 'investor', 'signer', 'tenant_admin']}
              >
                <UnifiedPropertyProcess />
              </ProtectedRoute>
            }
          />
          {/* Legacy redirects (kept for bookmarks/links) */}
          <Route
            path="/sales/properties/:id/edit"
            element={<Navigate to=".." replace relative="path" />}
          />
          <Route
            path="/sales/properties/:id/unified"
            element={<Navigate to=".." replace relative="path" />}
          />
          {/* Listings Routes */}
          <Route
            path="/sales/listings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ListingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/listings/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ListingDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Bearbeiten-Seite aufgeloest (#1032): Stammdaten und Objektliste
              liegen auf der Teaser-Hauptseite — alte Lesezeichen landen dort. */}
          <Route
            path="/sales/listings/:id/edit"
            element={<Navigate to=".." replace relative="path" />}
          />
          <Route
            path="/sales/contracts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ContractsListPage />
              </ProtectedRoute>
            }
          />
          <Route path="/sales/contracts/new" element={<Navigate to="/sales/contracts/wizard-v3" replace />} />
          <Route path="/sales/contracts/wizard" element={<Navigate to="/sales/contracts/wizard-v3" replace />} />
          <Route
            path="/sales/contracts/wizard-v3"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ContractWizardV3Unified />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/contracts/search-mandate"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <SearchMandateWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/contracts/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ContractDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/contracts/:id/countersign"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CountersignPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/contacts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ContactsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/sales/google-import" element={<Navigate to="/crm/contacts/import" replace />} />
          <Route path="/sales/microsoft-import" element={<Navigate to="/crm/contacts/import" replace />} />
          <Route
            path="/sales/campaigns"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CampaignsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/campaigns/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CampaignCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/campaigns/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CampaignDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/campaigns/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CampaignCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/quality-gate"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <QualityGatePage />
              </ProtectedRoute>
            }
          />
          {/* Recherche-Leads sind mit #1225 unqualifizierte Organisationen */}
          <Route path="/admin/research-leads" element={<Navigate to="/crm/organizations?qualification=unqualified" replace />} />
          <Route path="/admin/linkedin-import" element={<Navigate to="/crm/contacts/import" replace />} />
          <Route path="/admin/contact-dedup" element={<Navigate to="/crm/contacts/dedup" replace />} />
          <Route
            path="/admin/ceo-agent"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CEOAgentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/email-templates"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <EmailTemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/mandanten"
            element={
              <ProtectedRoute allowedRoles={['admin', 'tenant_admin']}>
                <MandantenPage />
              </ProtectedRoute>
            }
          />
          {/* Investor Routes */}
          <Route
            path="/investor"
            element={<Navigate to="/investor/search" replace />}
          />
          <Route
            path="/investor/search"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <SearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/favorites"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <FavoritesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/offers"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <MyOffersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/profile"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor', 'developer']}>
                <SearchProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/analyses"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <AnalysesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/analyses/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <AnalysisNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/analyses/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <AnalysesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/contracts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <InvestorContractsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/contracts/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <InvestorContractDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/properties/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <InvestorPropertyDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Legacy redirects (alte Mails/Lesezeichen) — Teaser und Dossier
              liegen auf der Objekt-Hauptseite. `relative="path"` ist zwingend:
              ohne den Zusatz löst `..` relativ zur Route auf und landet auf «/». */}
          <Route
            path="/investor/properties/:id/detail"
            element={<Navigate to=".." replace relative="path" />}
          />
          <Route
            path="/investor/properties/:id/dossier"
            element={<Navigate to=".." replace relative="path" />}
          />
          {/* Listing-Based Investor Routes (Phase 3) */}
          <Route
            path="/investor/listings/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <ListingTeaserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor/listings/:id/dossier"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <ListingDossierPage />
              </ProtectedRoute>
            }
          />
          <Route path="/investor/settings" element={<Navigate to="/settings" replace />} />

          {/* Seller Routes */}
          <Route
            path="/seller/properties"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <SellerPropertiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller/properties/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <UnifiedPropertyProcess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller/properties/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <UnifiedPropertyProcess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller/properties/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'investor']}>
                <UnifiedPropertyProcess />
              </ProtectedRoute>
            }
          />

          {/* CRM Routes (all admin/sales users) */}
          <Route
            path="/crm/organizations"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CRMOrganizationsPage />
              </ProtectedRoute>
            }
          />
          {/* Fondsradar — alle Anlagevehikel mit ihren Signalen (#1232) */}
          <Route
            path="/crm/funds"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <FundRadarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/organizations/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <OrganizationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/contacts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CRMContactsPage />
              </ProtectedRoute>
            }
          />
          {/* Kontaktimport und Deduplizierung — eigene Seiten (#1226) */}
          <Route
            path="/crm/contacts/import"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ContactImportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/contacts/dedup"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ContactDedupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/contacts/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <ContactDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/activities"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CRMActivitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/campaigns"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales']}>
                <CrmCampaignsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Dev Command & Control (admin-only, own layout) */}
        <Route
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DevLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dev" element={<DevCommandCenter />} />
          <Route path="/dev/:taskId" element={<DevCommandCenter />} />
        </Route>

        {/* Public Pages (no auth required) */}
        <Route path="/sign/:token" element={<PublicSigningPage />} />
        <Route path="/sign/:token/pdf" element={<PublicSigningDocumentPage variant="pdf" />} />
        <Route path="/sign/:token/agb" element={<PublicSigningDocumentPage variant="agb" />} />
        <Route path="/onboarding/:token" element={<OnboardingPage />} />
        <Route path="/teaser/:token" element={<PublicTeaserPage />} />
        <Route path="/agb" element={<AgbPage />} />
        <Route path="/help" element={<HelpPage />} />
        {/* Identification on the phone — opened from the QR code of the
            dialog, no login (STORY-1071). Lives in production too. */}
        <Route path="/kyc/mobile/:token" element={<KycMobilePage />} />
        {/* Signing on the phone — opened from the QR code of a signature
            field, no login (STORY-1111). */}
        <Route path="/sign-mobile/:code" element={<SignMobilePage />} />
        {/* Ident-provider stub — dev/staging only, never routed in production */}
        {KYC_STUB_ROUTE_ENABLED && (
          <Route path="/kyc/stub/:ref" element={<KycStubPage />} />
        )}

        {/* Redirect root to login or dashboard */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* 404 - Redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  )
}

export default App
